import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { RootState } from "us.helper/types";
import { workflowAction } from "us.collection/actions";
import { PlusOutlined } from "us.icons";
import { StateDelete, WorkflowGroup } from "us.collection/repository";
import { getEntityNumberAndType } from "us.collection/components/BMD/Functions";
import { matchPath, useLocation } from "react-router-dom";
import { IEvents } from "us.collection/reducers/Workflow/Interfaces";
import { NewEvent, EventItem } from "./Components";
import {
  eventDrawerInitial,
  ItemMenuActions,
  WORKFLOW_TOOL_DOWNLOAD_URL,
  WorkflowSequenceType,
  WorkflowSequenceCategoryId,
  WorkflowStateStatus,
  WorkflowViewType
} from "us.collection/components/Workflow/Constants";
import { searchEvent } from "us.collection/components/Workflow/Functions";
import { useFormikContext } from "formik";
import { openWorkflowTool } from "us.collection/functions";
import { EntityType } from "us.common/constants";
import {
  updateSequence,
  getAllSelectedWorkflows,
  isAllEventWorkflowsSelected,
  isEventSelectionEnabled,
  isSelectedEventExportVisible,
} from "us.collection/components/Workflow/Functions";
import { SaveSequence } from "us.collection/components/Workflow/Repository";
import { useDndScrolling } from "react-dnd-scrolling";
import { EventTabProps } from "us.collection/components/Workflow/Interfaces";
import { useDetectClickOutside } from "react-detect-click-outside";
import { URLType } from "us.collection/constants";
import "us.collection/components/Workflow/Home.scss";
import _ from "lodash";

const { $Button, $Divider, $Drawer, $Skeleton, $Checkbox } = Common.Components;

/**
 * @description - The component used in view of events
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3116466232/Manage+Workflow+Events+-+UI
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 17/11/2022
 */
const Event: React.FC<EventTabProps & PropsFromRedux> = (props) => {
  const { t } = useTranslation();

  const {
    getEvents,
    workflowEvents,
    eventDrawer,
    exportWorkflowFiles,
    exportWorkflows,
    saveSequence,
    openDrawer,
    deleteEvent,
    isDeleting,
    metaData,
  } = props;

  const { caseId } = metaData.data ?? {};

  const [popoverOpenId, setPopoverOpenId] = useState(-1);
  const [isDownloadPopupVisible, setDownloadPopupVisible] =
    useState<boolean>(false);

  const { values, setFieldValue, setValues } = useFormikContext<any>();
  const searchFilters = _.omit(values, "statesAndEvent");

  const { pathname } = useLocation();
  const { params }: any = matchPath( {
    path: "/:type/:id?/:module?",
    end: false,
    caseSensitive: false,
  },pathname);

  const entityData = WorkflowGroup.call(
    getEntityNumberAndType(
      params["type"],
      params["id"],
      caseId?.toString() ?? ""
    )
  );

  const caseScrollRef = useDetectClickOutside({
    onTriggered: () => handleDeselectOnOutsideClick(),
    disableClick:
      values?.selectionEnabledCategory == null ||
      values?.selectionEnabledCategory != EntityType.CASE,
  });

  const subCaseScrollRef = useDetectClickOutside({
    onTriggered: () => handleDeselectOnOutsideClick(),
    disableClick:
      values?.selectionEnabledCategory == null ||
      values?.selectionEnabledCategory != EntityType.SUB_CASE,
  });

  useDndScrolling(caseScrollRef, {});
  useDndScrolling(subCaseScrollRef, {});

  useEffect(() => {
    if (params["type"] && params["id"]) {
      workflowEvents?.data?.length == 0 &&
        getEvents &&
        getEvents(
          WorkflowGroup.call(
            getEntityNumberAndType(params["type"], params["id"], "")
          )
        );
    }
  }, []);

  useMemo(() => {
    if (values?.searchText || values?.activeEvents != WorkflowStateStatus.ALL) {
      values?.isDraggable && setFieldValue("isDraggable", false);
    }
  }, [values?.searchText, values?.activeEvents]);

  const { caseEvents, subCaseEvents } = useMemo(() => {
    return searchEvent(workflowEvents?.data, searchFilters);
  }, [workflowEvents?.data, searchFilters]);

  /**
   * Deselect action when user click outside of case selection or subCase selection
   */
  const handleDeselectOnOutsideClick = () => {
    values?.selectionEnabledCategory != null &&
      values?.selectedWorkflows?.length > 0 &&
      setValues((previousValues: any) => ({
        ...previousValues,
        selectedWorkflows: [],
        selectionEnabledCategory: null,
      }));
  };

  /**
   * Delete event
   * @param id Event id
   */
  const deleteWorkflowEvent = (id: number, categoryId: number) => {
    deleteEvent &&
      deleteEvent({
        ...StateDelete.call({ id, categoryId }),
        stateRequest: WorkflowGroup.call(
          getEntityNumberAndType(params["type"], params["id"], "")
        ),
        searchFilters,
      });
    setPopoverOpenId(-1);
  };

  /**
   * @description handle events menu actions
   * @param action - action of clicked menu icon
   * @param record - selected record for action
   */
  const handleEventAction = async (action: ItemMenuActions, record: any) => {
    const {
      id,
      stateGroupId,
      workflowGroupStateId,
      displayName,
      stateGroupName,
    } = record ?? {};

    switch (action) {
      case ItemMenuActions.OPEN_WORKFLOW:
        const socketStatus = await openWorkflowTool({
          ...record,
          ...entityData,
        });
        setDownloadPopupVisible(socketStatus);
        break;
      case ItemMenuActions.DOWNLOAD:
        window.open(WORKFLOW_TOOL_DOWNLOAD_URL, "_self");
        setPopoverOpenId(-1);
        break;
      case ItemMenuActions.EXPORT_WORKFLOW_FILE:
        exportWorkflows &&
          exportWorkflows([
            {
              workflowGroupStateId,
              stateGroupId,
              displayName,
              stateGroupName,
            },
          ]);
        break;
      case ItemMenuActions.EDIT:
        openDrawer &&
          openDrawer({
            title: "US.COLLECTION.CASE:WORKFLOWS.EDIT_EVENT",
            isVisible: true,
            isEdit: true,
            eventId: id,
            workflowGroupStateId,
          });
        setPopoverOpenId(-1);
        break;
      case ItemMenuActions.DELETE:
        deleteWorkflowEvent(id, stateGroupId);
        break;
      default:
        break;
    }
  };

  /**
   * @description Update sequence of events
   * @param fromIndex source index
   * @param toIndex target index
   * @param workflowType workflow type
   */
  const updateEventSequence = (
    fromIndex: number,
    toIndex: number,
    workflowType: string
  ) => {
    const sequenceList = updateSequence(
      fromIndex,
      toIndex,
      workflowType == EntityType.CASE ? caseEvents : subCaseEvents,
      WorkflowViewType.OTHER
    );
    const saveParams = SaveSequence.call({
      type: WorkflowSequenceType.EVENT,
      categoryId:
        workflowType == EntityType.CASE
          ? WorkflowSequenceCategoryId.CASE
          : WorkflowSequenceCategoryId.SUB_CASE,
      sequenceList,
    });
    saveSequence && saveSequence({ sequenceList, saveParams });
  };

  return (
    <>
      <$Skeleton
        loading={workflowEvents?.isLoading}
        active
        paragraph={{ rows: 2 }}
        className={workflowEvents?.isLoading ? "p-3" : ""}
      >
        <div>
          <div className="workflows-events-layout">
            <div className="workflows-events-layout-col">
              <div className="d-flex align-items-center justify-content-between pr-4">
                <div
                  className="d-flex align-items-center"
                  style={{ paddingLeft: "10px" }}
                >
                  {caseEvents?.length > 0 &&
                    values?.selectionEnabledCategory == EntityType.CASE && (
                      <$Checkbox
                        name={"caseEvent"}
                        checked={isAllEventWorkflowsSelected(
                          values?.selectedWorkflows,
                          EntityType.CASE,
                          caseEvents
                        )}
                        className="mr-3"
                        onChange={(e: any) => {
                          e.stopPropagation();
                          setFieldValue(
                            "selectedWorkflows",
                            getAllSelectedWorkflows(
                              caseEvents,
                              e.target.checked,
                              "event"
                            )
                          );
                        }}
                      />
                    )}
                  <h3 className="mb-0">
                    {t("US.COLLECTION.CASE:WORKFLOWS.CASE_EVENTS")}
                  </h3>
                  <$Divider className="bui-devider" type="vertical" />
                  <$Button
                    type="default"
                    size="small"
                    icon={<PlusOutlined />}
                    disabled={isDeleting}
                    onClick={() =>
                      openDrawer &&
                      openDrawer({
                        ...eventDrawerInitial,
                        title: "US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_EVENT",
                        isVisible: true,
                      })
                    }
                  >
                    {t("US.COLLECTION.CASE:WORKFLOWS.NEW_EVENT")}
                  </$Button>
                </div>
                <div className="d-flex align-items-center">
                  {isEventSelectionEnabled(
                    values,
                    EntityType.CASE,
                    caseEvents
                  ) && params["type"] == URLType.BUREAU && (
                    <$Button
                      type="link"
                      size="small"
                      className="we-download-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFieldValue("selectionEnabledCategory", EntityType.CASE);
                        setFieldValue(
                          "selectedWorkflows",
                          getAllSelectedWorkflows(caseEvents, true, "event")
                        );
                      }}
                    >
                      {t("US.COMMON:COMMON.SELECT")}
                    </$Button>
                  )}
                  {isSelectedEventExportVisible(
                    values,
                    EntityType.CASE,
                    caseEvents
                  ) && (
                    <$Button
                      type="link"
                      size="small"
                      onClick={() => exportWorkflowFiles()}
                    >
                      {t("US.COMMON:COMMON.EXPORT")}
                    </$Button>
                  )}
                </div>
              </div>
              <div ref={caseScrollRef} className="ws-cat-wrap mt-4">
                <div className="pr-3">
                  <div className="ws-tile-container">
                    {caseEvents?.map((event: IEvents, index: number) => {
                      return (
                        <EventItem
                          key={event?.id}
                          index={index}
                          eventRecord={event}
                          isDownloadPopupVisible={isDownloadPopupVisible}
                          popoverOpenId={popoverOpenId}
                          onChangeDownloadPopup={(isVisible) =>
                            setDownloadPopupVisible(isVisible)
                          }
                          onChangePopup={(visibleId) =>
                            setPopoverOpenId(visibleId)
                          }
                          onChangeEventAction={handleEventAction}
                          onMove={updateEventSequence}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="workflows-events-layout-col">
              <div className="d-flex align-items-center justify-content-between pr-4">
                <div
                  className="d-flex align-items-center"
                  style={{ paddingLeft: "10px" }}
                >
                  {subCaseEvents?.length > 0 &&
                    values?.selectionEnabledCategory == EntityType.SUB_CASE && (
                      <$Checkbox
                        name={"SubCaseEvent"}
                        checked={isAllEventWorkflowsSelected(
                          values?.selectedWorkflows,
                          EntityType.SUB_CASE,
                          subCaseEvents
                        )}
                        className="mr-3"
                        onChange={(e: any) => {
                          e.stopPropagation();
                          setFieldValue(
                            "selectedWorkflows",
                            getAllSelectedWorkflows(
                              subCaseEvents,
                              e.target.checked,
                              "event"
                            )
                          );
                        }}
                      />
                    )}
                  <h3 className="mb-0">
                    {t("US.COLLECTION.CASE:WORKFLOWS.SUBCASE_EVENTS")}
                  </h3>
                  <$Divider className="bui-devider" type="vertical" />
                  <$Button
                    type="default"
                    size="small"
                    icon={<PlusOutlined />}
                    disabled={isDeleting}
                    onClick={() =>
                      openDrawer &&
                      openDrawer({
                        ...eventDrawerInitial,
                        title: "US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_EVENT",
                        isVisible: true,
                      })
                    }
                  >
                    {t("US.COLLECTION.CASE:WORKFLOWS.NEW_EVENT")}
                  </$Button>
                </div>
                <div className="d-flex align-items-center">
                  {isEventSelectionEnabled(
                    values,
                    EntityType.SUB_CASE,
                    subCaseEvents
                  ) && params["type"] == URLType.BUREAU &&  (
                    <$Button
                      type="link"
                      size="small"
                      className="we-download-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFieldValue("selectionEnabledCategory", EntityType.SUB_CASE);
                        setFieldValue(
                          "selectedWorkflows",
                          getAllSelectedWorkflows(subCaseEvents, true, "event")
                        );
                      }}
                    >
                      {t("US.COMMON:COMMON.SELECT")}
                    </$Button>
                  )}
                  {isSelectedEventExportVisible(
                    values,
                    EntityType.SUB_CASE,
                    subCaseEvents
                  ) && (
                    <$Button
                      type="link"
                      size="small"
                      onClick={() => exportWorkflowFiles()}
                    >
                      {t("US.COMMON:COMMON.EXPORT")}
                    </$Button>
                  )}
                </div>
              </div>
              <div ref={subCaseScrollRef} className="ws-cat-wrap mt-4">
                <div className="pr-3">
                  <div className="ws-tile-container">
                    {subCaseEvents?.map((event: IEvents, index: number) => {
                      return (
                        <EventItem
                          key={event?.id}
                          index={index}
                          eventRecord={event}
                          isDownloadPopupVisible={isDownloadPopupVisible}
                          popoverOpenId={popoverOpenId}
                          onChangeDownloadPopup={(isVisible) =>
                            setDownloadPopupVisible(isVisible)
                          }
                          onChangePopup={(visibleId) =>
                            setPopoverOpenId(visibleId)
                          }
                          onChangeEventAction={handleEventAction}
                          onMove={updateEventSequence}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <$Drawer
          title={t(eventDrawer.title)}
          width={500}
          open={eventDrawer.isVisible}
          onClose={() => openDrawer && openDrawer(eventDrawerInitial)}
          destroyOnClose
        >
          <NewEvent searchFilters={searchFilters} />
        </$Drawer>
      </$Skeleton>
    </>
  );
};

const { eventsByAccessLevel, event, eventsDelete, sequence, exportWorkflow } =
  workflowAction;

const mapStateToProps = (state: RootState) => {
  const { Workflow, domainView } = state;
  const { workflowEvents, eventDrawer, isDeleting } = Workflow;
  const { metaData } = domainView;

  return {
    workflowEvents,
    eventDrawer,
    isDeleting,
    metaData,
  };
};

const mapDispatchToProps = {
  getEvents: eventsByAccessLevel.get,
  openDrawer: event.openDrawer,
  deleteEvent: eventsDelete.delete,
  saveSequence: sequence.save,
  exportWorkflows: exportWorkflow.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Event);
